import "./TicketDetailsDialog.css";
import React, { useEffect, useState, useCallback } from "react";

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In Progress",
  closed: "Closed",
};

const PRIORITY_LABELS = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

const TICKET_TYPE_OPTIONS = [
  { value: "Fix", label: "Fix" },
  { value: "New Build", label: "New Build" },
];

const getTicketTypeLabel = (value) =>
  TICKET_TYPE_OPTIONS.find((option) => option.value === value)?.label || "Fix";

const getTicketTypeToneClass = (value) =>
  String(value || "Fix").trim().toLowerCase().replace(/\s+/g, "-");

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
};

const isImageKey = (key) =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(key || "");

const EMAIL_FOOTER_TRIGGER_RE =
  /^(--+\s*$|_{3,}\s*$|confidentiality notice|disclaimer[:\s]|privileged and confidential|this (e-?mail|message|transmission|communication)\b.*(confidential|privileged|intended (solely|only) for|hipaa)|hipaa)/i;

const splitEmailFooter = (text) => {
  const body = String(text || "");
  const lines = body.split("\n");

  for (let i = 0; i < lines.length; i += 1) {
    const trimmed = lines[i].trim();
    if (!trimmed) continue;

    if (EMAIL_FOOTER_TRIGGER_RE.test(trimmed)) {
      // Split here even if main ends up empty (e.g. a reply that's nothing but a signature
      // block) - skipping an empty-main match used to keep scanning past the real footer
      // boundary, letting disclaimer/signature text merge back into the main body instead of
      // rendering as its own footer block.
      const main = lines.slice(0, i).join("\n").trim();
      const footer = lines.slice(i).join("\n").trim();
      return { main, footer };
    }
  }

  return { main: body.trim(), footer: "" };
};

const getTicketAttachments = (ticket) => {
  if (Array.isArray(ticket?.attachments) && ticket.attachments.length > 0) {
    return ticket.attachments;
  }
  if (ticket?.attachmentUrl) {
    return [{ key: ticket.attachmentKey || "", name: ticket.attachmentName || "attachment", url: ticket.attachmentUrl }];
  }
  return [];
};

const buildCopyPayload = (ticket) =>
  [
    ticket?.requesterName || "",
    ticket?.email || "",
    ticket?.issue || "",
    PRIORITY_LABELS[ticket?.priority] || "Medium",
    ticket?.description || "",
  ]
    .filter(Boolean)
    .join("\n");

const TicketDetailsDialog = ({
  open,
  ticket,
  onClose,
  adminMode = false,
  assigneeSuggestions = [],
  openAssignmentOnOpen = false,
  onOpenAssignmentHandled,
  onStatusChange,
  onCommentSubmit,
  onEmailSend,
  onAssignTicket,
  onTicketTypeChange,
}) => {
  const [selectedStatus, setSelectedStatus] = useState("open");
  const [selectedTicketType, setSelectedTicketType] = useState("Fix");
  const [assignmentEmail, setAssignmentEmail] = useState("");
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [typeSaving, setTypeSaving] = useState(false);
  const [commentSaving, setCommentSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [localError, setLocalError] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedStatus(ticket?.status || "open");
      setSelectedTicketType(ticket?.ticketType || ticket?.type || "Fix");
      setAssignmentEmail(ticket?.assignedToEmail || "");
      setAssignmentOpen(Boolean(openAssignmentOnOpen));
      setAssignmentSaving(false);
      setCommentText("");
      setStatusSaving(false);
      setTypeSaving(false);
      setCommentSaving(false);
      setCopied(false);
      setLocalError("");
      setLightboxIndex(null);
      setEmailSubject(ticket?.issue || "");
      setEmailBody("");
      setEmailSending(false);
      if (openAssignmentOnOpen && typeof onOpenAssignmentHandled === "function") {
        onOpenAssignmentHandled();
      }
    }
  }, [open, ticket, openAssignmentOnOpen, onOpenAssignmentHandled]);

  const ticketAttachments = getTicketAttachments(ticket);

  const handleLightboxKey = useCallback((event) => {
    if (lightboxIndex === null) return;
    if (event.key === "Escape") setLightboxIndex(null);
    if (event.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % ticketAttachments.length);
    if (event.key === "ArrowLeft") setLightboxIndex((i) => (i - 1 + ticketAttachments.length) % ticketAttachments.length);
  }, [lightboxIndex, ticketAttachments.length]);

  useEffect(() => {
    window.addEventListener("keydown", handleLightboxKey);
    return () => window.removeEventListener("keydown", handleLightboxKey);
  }, [handleLightboxKey]);

  if (!open || !ticket) {
    return null;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildCopyPayload(ticket));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      setLocalError("Unable to copy ticket details");
    }
  };

  const handleStatusSave = async () => {
    if (!adminMode || !onStatusChange || selectedStatus === ticket.status) {
      return;
    }

    try {
      setStatusSaving(true);
      setLocalError("");
      await onStatusChange(selectedStatus);
    } catch (error) {
      setLocalError(error.message || "Unable to update ticket status");
    } finally {
      setStatusSaving(false);
    }
  };

  const handleCommentSave = async () => {
    if (!adminMode || !onCommentSubmit || !commentText.trim()) {
      return;
    }

    try {
      setCommentSaving(true);
      setLocalError("");
      await onCommentSubmit(commentText.trim());
      setCommentText("");
    } catch (error) {
      setLocalError(error.message || "Unable to add comment");
    } finally {
      setCommentSaving(false);
    }
  };

  const handleTypeSave = async () => {
    if (
      !adminMode ||
      !onTicketTypeChange ||
      selectedTicketType === (ticket.ticketType || ticket.type)
    ) {
      return;
    }

    try {
      setTypeSaving(true);
      setLocalError("");
      await onTicketTypeChange(selectedTicketType);
    } catch (error) {
      setLocalError(error.message || "Unable to update ticket type");
    } finally {
      setTypeSaving(false);
    }
  };

  const handleAssignmentSave = async () => {
    if (
      !adminMode ||
      !onAssignTicket ||
      !assignmentEmail.trim()
    ) {
      return;
    }

    try {
      setAssignmentSaving(true);
      setLocalError("");
      await onAssignTicket({
        assigneeEmail: assignmentEmail.trim(),
      });
      setAssignmentOpen(false);
    } catch (error) {
      setLocalError(error.message || "Unable to assign ticket");
    } finally {
      setAssignmentSaving(false);
    }
  };

  const handleEmailSave = async () => {
    if (!adminMode || !onEmailSend || !emailBody.trim()) {
      return;
    }

    try {
      setEmailSending(true);
      setLocalError("");
      await onEmailSend({
        subject: emailSubject.trim() || ticket.issue || "Support ticket update",
        bodyText: emailBody.trim(),
      });
      setEmailBody("");
    } catch (error) {
      setLocalError(error.message || "Unable to send email");
    } finally {
      setEmailSending(false);
    }
  };

  // Newest first - the API/ticket object doesn't guarantee an order, and previously rendered
  // exactly as received (typically oldest-first insertion order), putting the latest email at
  // the bottom of the thread instead of the top.
  const emailThread = Array.isArray(ticket.emailThread)
    ? [...ticket.emailThread].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    : [];

  return (
    <div className="ticket-dialog-backdrop" onClick={onClose}>
      <div
        className="ticket-dialog-panel"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="ticket-dialog-header">
          <span>{`TKT-${ticket.ticketId} Details`}</span>
          <button
            type="button"
            className="ticket-dialog-close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="ticket-dialog-body">
          {localError ? (
            <div className="support-inline-error">{localError}</div>
          ) : null}

          <section className="ticket-dialog-section">
            <div className="ticket-dialog-section-title-row">
              <h4 className="ticket-dialog-section-title">Requester &amp; Issue</h4>
              {adminMode ? (
                <button
                  type="button"
                  className="ticket-copy-button"
                  onClick={handleCopy}
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              ) : null}
            </div>
            <div className="ticket-dialog-detail-box">
              <div className="ticket-dialog-detail-grid ticket-dialog-detail-grid--compact">
                <div>
                  <span className="ticket-dialog-label">First Name</span>
                  <p>{ticket.firstName || "-"}</p>
                </div>
                <div>
                  <span className="ticket-dialog-label">Last Name</span>
                  <p>{ticket.lastName || "-"}</p>
                </div>
                <div className="ticket-dialog-detail-grid__full">
                  <span className="ticket-dialog-label">Email</span>
                  <p>{ticket.email || "-"}</p>
                </div>
                <div>
                  <span className="ticket-dialog-label">Issue</span>
                  <p>{ticket.issue || "-"}</p>
                </div>
                <div>
                  <span className="ticket-dialog-label">Status</span>
                  <div className={`ticket-status-badge status-${ticket.status}`}>
                    {STATUS_LABELS[ticket.status] || ticket.status || "-"}
                  </div>
                </div>
                <div>
                  <span className="ticket-dialog-label">Priority</span>
                  <div
                    className={`ticket-priority-badge priority-${
                      ticket.priority || "medium"
                    }`}
                  >
                    {PRIORITY_LABELS[ticket.priority] || "Medium"}
                  </div>
                </div>
                <div>
                  <span className="ticket-dialog-label">Ticket Type</span>
                  <div
                    className={`ticket-type-pill ticket-type-pill--${
                      getTicketTypeToneClass(ticket.ticketType || ticket.type)
                    }`}
                  >
                    {getTicketTypeLabel(ticket.ticketType || ticket.type)}
                  </div>
                </div>
                <div>
                  <span className="ticket-dialog-label">Created</span>
                  <p>{formatDateTime(ticket.createdAt)}</p>
                </div>
                <div>
                  <span className="ticket-dialog-label">Updated</span>
                  <p>{formatDateTime(ticket.updatedAt)}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="ticket-dialog-section">
            <h4 className="ticket-dialog-section-title">Description</h4>
            <div className="ticket-dialog-detail-box">
              <p className="ticket-dialog-description">
                {ticket.description || "-"}
              </p>
            </div>
          </section>

          <section className="ticket-dialog-section">
            <h4 className="ticket-dialog-section-title">
              {`Attachments${ticketAttachments.length > 0 ? ` (${ticketAttachments.length})` : ""}`}
            </h4>
            <div className="ticket-dialog-detail-box">
              {ticketAttachments.length > 0 ? (
                <div className="ticket-attachment-stack">
                  {ticketAttachments.map((att, index) => (
                    <div key={att.key || index} className="ticket-attachment-item">
                      {isImageKey(att.key || att.name) ? (
                        <button
                          type="button"
                          className="ticket-attachment-image-btn"
                          onClick={() => setLightboxIndex(index)}
                        >
                          <img
                            className="ticket-attachment-image"
                            src={att.url}
                            alt={att.name || `Attachment ${index + 1}`}
                          />
                        </button>
                      ) : null}
                      <a
                        className="ticket-attachment-link"
                        href={att.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {att.name || `Attachment ${index + 1}`}
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="ticket-dialog-muted">No attachment provided.</p>
              )}
            </div>
          </section>

          {lightboxIndex !== null && ticketAttachments[lightboxIndex] ? (
            <div
              className="ticket-lightbox-backdrop"
              onClick={() => setLightboxIndex(null)}
            >
              <div
                className="ticket-lightbox-panel"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="ticket-lightbox-close"
                  onClick={() => setLightboxIndex(null)}
                >
                  ×
                </button>
                {ticketAttachments.length > 1 && (
                  <button
                    type="button"
                    className="ticket-lightbox-nav ticket-lightbox-nav--prev"
                    onClick={() => setLightboxIndex((i) => (i - 1 + ticketAttachments.length) % ticketAttachments.length)}
                  >
                    ‹
                  </button>
                )}
                <img
                  className="ticket-lightbox-img"
                  src={ticketAttachments[lightboxIndex].url}
                  alt={ticketAttachments[lightboxIndex].name || "Attachment"}
                />
                {ticketAttachments.length > 1 && (
                  <button
                    type="button"
                    className="ticket-lightbox-nav ticket-lightbox-nav--next"
                    onClick={() => setLightboxIndex((i) => (i + 1) % ticketAttachments.length)}
                  >
                    ›
                  </button>
                )}
                <div className="ticket-lightbox-caption">
                  {ticketAttachments[lightboxIndex].name || ""}
                  {ticketAttachments.length > 1 && (
                    <span className="ticket-lightbox-counter">{` ${lightboxIndex + 1} / ${ticketAttachments.length}`}</span>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {adminMode ? (
            <section className="ticket-dialog-section">
              <div className="ticket-dialog-section-title-row">
                <h4 className="ticket-dialog-section-title">Assign To</h4>
                <button
                  type="button"
                  className="ticket-copy-button"
                  onClick={() => setAssignmentOpen((currentValue) => !currentValue)}
                >
                  {assignmentOpen ? "Hide" : "Assign To"}
                </button>
              </div>
              <div className="ticket-dialog-detail-box">
                <div className="ticket-assignment-summary">
                  <span className="ticket-dialog-label">Current Assignee</span>
                  <p>{ticket.assignedToLabel || "Unassigned"}</p>
                </div>
                {assignmentOpen ? (
                  <div className="ticket-assignment-form">
                    <input
                      className="support-field"
                      list="ticket-assignee-suggestions"
                      placeholder="Type assignee email"
                      value={assignmentEmail}
                      onChange={(event) => setAssignmentEmail(event.target.value)}
                    />
                    <datalist id="ticket-assignee-suggestions">
                      {assigneeSuggestions.map((entry) => (
                        <option key={entry.email} value={entry.email}>
                          {entry.label || entry.email}
                        </option>
                      ))}
                    </datalist>
                    <button
                      type="button"
                      className="ticket-action-button ticket-action-button--full"
                      disabled={assignmentSaving || !assignmentEmail.trim()}
                      onClick={handleAssignmentSave}
                    >
                      {assignmentSaving ? "Saving..." : "Save Assignment"}
                    </button>
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {adminMode ? (
            <section className="ticket-dialog-section">
              <h4 className="ticket-dialog-section-title">Ticket Type</h4>
              <div className="ticket-dialog-detail-box">
                <div className="ticket-dialog-status-row">
                  <select
                    className="ticket-status-select"
                    value={selectedTicketType}
                    onChange={(event) => setSelectedTicketType(event.target.value)}
                  >
                    {TICKET_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="ticket-action-button"
                    disabled={
                      typeSaving ||
                      selectedTicketType === (ticket.ticketType || ticket.type)
                    }
                    onClick={handleTypeSave}
                  >
                    {typeSaving ? "Saving..." : "Save Type"}
                  </button>
                </div>
              </div>
            </section>
          ) : null}

          {adminMode ? (
            <section className="ticket-dialog-section">
              <h4 className="ticket-dialog-section-title">Update Status</h4>
              <div className="ticket-dialog-detail-box">
                <div className="ticket-dialog-status-row">
                  <select
                    className="ticket-status-select"
                    value={selectedStatus}
                    onChange={(event) => setSelectedStatus(event.target.value)}
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="closed">Closed</option>
                  </select>
                  <button
                    type="button"
                    className="ticket-action-button"
                    disabled={statusSaving || selectedStatus === ticket.status}
                    onClick={handleStatusSave}
                  >
                    {statusSaving ? "Saving..." : "Save Status"}
                  </button>
                </div>
              </div>
            </section>
          ) : null}

          <section className="ticket-dialog-section">
            <h4 className="ticket-dialog-section-title">Comments</h4>
            <div className="ticket-dialog-comments">
              {ticket.comments?.length ? (
                [...ticket.comments]
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map((comment) => (
                  <div
                    className="ticket-comment-card"
                    key={comment.commentId || `${comment.createdAt}-${comment.createdBy}`}
                  >
                    <div className="ticket-comment-meta">
                      <strong>{comment.createdBy || "Support Team"}</strong>
                      <span>{formatDateTime(comment.createdAt)}</span>
                    </div>
                    <p>{comment.commentText || "-"}</p>
                  </div>
                ))
              ) : (
                <div className="ticket-dialog-empty">No comments yet.</div>
              )}
            </div>
          </section>

          {adminMode ? (
            <section className="ticket-dialog-section">
              <h4 className="ticket-dialog-section-title">Email Chain</h4>
              <div className="ticket-dialog-detail-box">
                {emailThread.length ? (
                  <div className="ticket-email-thread-list">
                    {emailThread.map((message) => {
                      const { main, footer } = splitEmailFooter(message.bodyText);
                      return (
                        <div
                          className={`ticket-email-card ticket-email-card--${message.direction}`}
                          key={message.emailId || message.messageId || message.createdAt}
                        >
                          <div className="ticket-email-card-meta">
                            <strong>
                              {message.direction === "client_to_admin"
                                ? message.senderEmail || "Client"
                                : message.createdBy || "Support Team"}
                            </strong>
                            <span>{formatDateTime(message.createdAt)}</span>
                          </div>
                          <div className="ticket-email-card-subject">
                            {message.subject || "-"}
                          </div>
                          <p>{main || "-"}</p>
                          {footer ? (
                            <p className="ticket-email-card-footer">{footer}</p>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="ticket-dialog-empty">
                    No email chain started yet.
                  </div>
                )}
              </div>
            </section>
          ) : null}

          {adminMode ? (
            <section className="ticket-dialog-section">
              <h4 className="ticket-dialog-section-title">
                {emailThread.length ? "Send Reply" : "Start Email Chain"}
              </h4>
              <div className="ticket-dialog-detail-box">
                <input
                  className="support-field"
                  placeholder="Email subject"
                  value={emailSubject}
                  onChange={(event) => setEmailSubject(event.target.value)}
                />
                <textarea
                  className="ticket-comment-input"
                  placeholder="Write the email message for the client"
                  value={emailBody}
                  onChange={(event) => setEmailBody(event.target.value)}
                />
                <button
                  type="button"
                  className="ticket-action-button ticket-action-button--full"
                  disabled={emailSending || !emailBody.trim()}
                  onClick={handleEmailSave}
                >
                  {emailSending
                    ? "Sending..."
                    : emailThread.length
                      ? "Send Reply"
                      : "Send First Email"}
                </button>
              </div>
            </section>
          ) : null}

          {adminMode ? (
            <section className="ticket-dialog-section">
              <h4 className="ticket-dialog-section-title">Add Comment</h4>
              <div className="ticket-dialog-detail-box">
                <textarea
                  className="ticket-comment-input"
                  placeholder="Add a comment for this ticket"
                  value={commentText}
                  onChange={(event) => setCommentText(event.target.value)}
                />
                <button
                  type="button"
                  className="ticket-action-button ticket-action-button--full"
                  disabled={commentSaving || !commentText.trim()}
                  onClick={handleCommentSave}
                >
                  {commentSaving ? "Sending..." : "Add Comment"}
                </button>
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TicketDetailsDialog;

